import * as d3 from 'd3'

import { IElectionResultRow, IPartyNumberMap } from '../models/TypeHelper'
import { PredictionModel } from '../utils/ReduxStore'
import Candidate from '../models/Candidate'
import ElectionResultStore from './ElectionResultStore'
import NationalResult from '../models/NationalResult'
import Party from '../models/Party'
import PartyResult from '../models/PartyResult'
import PartyResultMap from '../models/PartyResultMap'
import Province from '../models/Province'
import RandomUniformSwingModel from './RandomUniformSwingModel'
import Region from '../models/Region'
import Riding from '../models/Riding'
import RidingResult from '../models/RidingResult'
import UniformSwingModel from './UniformSwingModel'

export const BASE_ELECTION_KEY = '2015-original'
export const BYELECTIONS_KEY = '2015-byelections'
export const ELECTION_2019_KEY = '2019-preliminary'

export default class DataController {
  protected static INSTANCE: DataController = new DataController()

  protected store: ElectionResultStore = new ElectionResultStore()

  protected constructor() {}

  public static instance(): DataController {
    return DataController.INSTANCE
  }

  public async initialize(loadByElections = false): Promise<NationalResult> {
    const nationalResult = await this.parseElectionResultsFromFile(
      './data/results/2015-results.csv'
    )
    this.store.add(BASE_ELECTION_KEY, nationalResult)

    const result2019 = await this.parseElectionResultsFromFile(
      './data/results/2019-results.csv'
    )
    this.store.add(ELECTION_2019_KEY, result2019)

    console.log(
      'result2019',
      result2019,
      DataController.instance().resultsForKey(ELECTION_2019_KEY)
    )

    if (loadByElections) {
      const nationalResultWithByElections = await this.addByElectionsFromFile(
        nationalResult,
        './data/results/2015-byelections.csv'
      )
      this.store.add(BYELECTIONS_KEY, nationalResultWithByElections)
      return this.store.resultsForKey(BYELECTIONS_KEY)
    } else {
      return this.store.resultsForKey(BASE_ELECTION_KEY)
    }
  }

  public static async loadElectionResults(
    url: string
  ): Promise<IElectionResultRow[]> {
    const data = await d3.csv(url)
    return data.map(
      (d): IElectionResultRow => (d as unknown) as IElectionResultRow
    )
  }

  public async parseElectionResultsFromFile(
    url: string
  ): Promise<NationalResult> {
    const ridingResultArray: RidingResult[] = []

    const rows = await DataController.loadElectionResults(url)

    // TODO: sort the rows so that Quebec comes first. This is only so that we
    // get the Bloc in the "correct" order relative to "Other".

    rows.forEach((row): void => {
      const province = Province.fromECName(row.province)
      const riding = new Riding(+row.edNum, row.edName, province)

      const partyResultMap = new PartyResultMap()
      Party.allPartiesArray().forEach((party: Party): void => {
        // Get candidate name and votes from the row
        const name = row[`${party.key}_name`]
        const votes = +row[`${party.key}_votes`]

        if (name) {
          // Set candidate
          const candidate = new Candidate(name, party, riding)
          riding.addCandidate(candidate)

          // Set party result
          const partyResult = PartyResult.fromPartyAndVote(party, votes)
          partyResultMap.setResult(partyResult)
        }
      })

      const result = new RidingResult(partyResultMap, riding)
      ridingResultArray.push(result)
    })

    return new NationalResult(ridingResultArray)
  }

  public async addByElectionsFromFile(
    nationalResult: NationalResult,
    url: string
  ): Promise<NationalResult> {
    const ridingResultArray: RidingResult[] = []

    const rows = await DataController.loadElectionResults(url)

    rows.forEach((row): void => {
      const province = Province.fromECName(row.province)
      const riding = new Riding(+row.edNum, row.edName, province)

      const partyResultMap = new PartyResultMap()
      Party.allPartiesArray().forEach((party: Party): void => {
        // Get candidate name and votes from the row
        const name = row[`${party.key}_name`]
        let votes = +row[`${party.key}_votes`]

        // // IFF 'OTH', also add in PPC if available
        // if (party.key === 'OTH') {
        //   votes += +row['PPC_votes']
        // }

        if (name) {
          // Set candidate
          const candidate = new Candidate(name, party, riding)
          riding.addCandidate(candidate)

          // Set party result
          const partyResult = PartyResult.fromPartyAndVote(party, votes)
          partyResultMap.setResult(partyResult)
        }
      })

      const result = new RidingResult(partyResultMap, riding)
      ridingResultArray.push(result)
    })

    nationalResult
      .ridingResults()
      .forEach((ridingResult: RidingResult): void => {
        if (
          ridingResultArray.find(
            (result: RidingResult): boolean =>
              result.riding.key === ridingResult.riding.key
          )
        ) {
          // No-op
        } else {
          ridingResultArray.push(ridingResult.clone())
        }
      })

    return new NationalResult(ridingResultArray)
  }

  public resultsForKey(electionKey: string): NationalResult {
    return this.store.resultsForKey(electionKey)
  }

  public saveResults(
    electionKey: string,
    nationalResult: NationalResult
  ): void {
    this.store.add(electionKey, nationalResult)
  }

  public reset(): void {
    this.store.reset()
  }

  public static swingResults(
    baseElectionKey: string,
    predictionModel: PredictionModel,
    newPolls: IPartyNumberMap,
    filter?: (ridingResult: RidingResult) => boolean,
    regionKey?: string,
    marginOfError?: number
  ): RidingResult[] {
    const instance = DataController.instance().resultsForKey(baseElectionKey)
    const baseResult = regionKey
      ? instance.resultForRegion(Region.fromKey(regionKey))
      : instance
    const oldPolls = baseResult.partyResultMap.votePercents()

    const differences =
      predictionModel === 'randomized'
        ? RandomUniformSwingModel.differences(oldPolls, newPolls, marginOfError)
        : UniformSwingModel.differences(oldPolls, newPolls)

    const filteredRidingResults = baseResult.ridingResults(filter)

    const newRidingResults = filteredRidingResults.map(
      (ridingResult): RidingResult =>
        UniformSwingModel.apply(ridingResult, differences)
    )

    return newRidingResults
  }
}

export const resultForKey = (key: string): NationalResult => {
  return DataController.instance().resultsForKey(key)
}

export const saveResult = (
  key: string,
  nationalResult: NationalResult
): void => {
  if (key && nationalResult) {
    DataController.instance().saveResults(key, nationalResult)
  }
}
